import { collection, doc, getDoc, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { auth, db, callable, requireFirebase } from './firebase.js';

const newId = () => crypto.randomUUID();
const uid = () => {
  requireFirebase();
  if (!auth.currentUser) throw new Error('Sign in before loading saved practice.');
  return auth.currentUser.uid;
};
const sessions = () => collection(db, 'users', uid(), 'sessions');
const serialise = value => {
  if (value?.toDate) return value.toDate().toISOString();
  if (Array.isArray(value)) return value.map(serialise);
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, serialise(v)]));
  return value;
};
const record = snapshot => ({ id: snapshot.id, ...serialise(snapshot.data()) });

export async function createPracticeSession({ mode, transport, group, scenarioId, waypoint, branch, heading, callsign, sessionId = newId() }) {
  return callable('createSession', { mode, transport, group, ...(scenarioId ? {scenarioId} : {}), ...(waypoint ? {waypoint} : {}), ...(branch ? {branch} : {}), ...(heading ? {heading} : {}), ...(callsign ? {callsign} : {}), sessionId });
}
export async function listSessions() {
  const result = await getDocs(query(sessions(), orderBy('createdAt', 'desc'), limit(50)));
  return result.docs.map(record);
}
export async function loadSession(sessionId) {
  const ref = doc(sessions(), sessionId);
  const [session, turns, attempts] = await Promise.all([
    getDoc(ref),
    getDocs(query(collection(ref, 'turns'), orderBy('sequence', 'asc'))),
    getDocs(query(collection(ref, 'attempts'), orderBy('createdAt', 'asc'))),
  ]);
  if (!session.exists()) throw new Error('Saved practice session not found.');
  return { session: record(session), turns: turns.docs.map(record), attempts: attempts.docs.map(record) };
}
export async function persistTurn(sessionId, event) {
  // Callers that retry must keep eventId unchanged. No acknowledgement is issued
  // until the authenticated server confirms the Firestore transaction.
  return callable('saveTurn', { ...event, eventId: event.eventId || newId(), sessionId });
}
export async function submitAttempt(sessionId, attempt) {
  return callable('submitAttempt', { ...attempt, eventId: attempt.eventId || newId(), sessionId });
}
export async function submitVoiceAttempt(sessionId, attempt) {
  return callable('submitVoiceAttempt', { ...attempt, sessionId }, { timeout: 90000 });
}
export async function updatePracticeSession(sessionId, patch) {
  return callable('updateSession', { ...patch, sessionId });
}
export async function requestLiveToken(sessionId, options) {
  return callable('getLiveToken', { ...options, sessionId });
}
export async function generatePracticeScenario(options) { return callable('generateScenario', options); }
export async function adminListTeams() { return callable('adminListTeams', {}); }
export async function adminCreateTeam(data) { return callable('adminCreateTeam', data); }
export async function adminUpdateTeam(data) { return callable('adminUpdateTeam', data); }
export async function adminListStudents(teamId) { return callable('adminListStudents', {teamId}); }
export async function adminInitializeTeams() { return callable('adminInitializeTeams', {}); }
export async function adminResetTeamCode(teamId) { return callable('adminResetTeamCode', {teamId}); }
export async function adminSetTeamEnabled(teamId, enabled) { return callable('adminSetTeamEnabled', {teamId, enabled}); }
export async function adminListSessions(teamId) { return callable('adminListSessions', {teamId}); }
export async function adminLoadSession(uid, sessionId) { return callable('adminLoadSession', {uid, sessionId}); }
